// HeroIllustration — animated SVG skyline for the welcome gate hero.
// Replaces the live-photo collage (user-approved divergence from the
// Flatty-handoff mockup, 2026-07-08): guaranteed visual quality, no
// dependency on listing photo quality or the search API being up.
//
// THEMED (user-directed 2026-07-08): the palette lives in --h-* CSS vars
// defined below per theme class (html.light/.dark/.hacker, see
// theme-provider.tsx). :root carries the light (morning) palette, .dark the
// original dusk, .hacker a terminal-green night with CRT scanlines. The
// welcome-gate veil + hero text consume --h-veil/--h-ink from the same block.
// SVG presentation attributes can't hold var(), so fills/strokes are set via
// the tiny classes in <style>.
//
// Motion: twinkling stars (hidden in light — daytime), swaying rooster
// weathervane (brand nod), chimney smoke — all CSS keyframes, disabled
// under prefers-reduced-motion.

export function HeroIllustration() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 1440 520"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
      focusable="false"
    >
      <style>{`
        :root{
          --h-sky1:#B7CEE0;--h-sky2:#C9DAE6;--h-sky3:#EBE3D2;--h-sky4:#F6D9AE;--h-sky5:#F2C186;
          --h-sun1:#F7BE6B;--h-sun2:#FBD489;--h-sun3:#FFF0C9;
          --h-far:#C7ADA2;--h-mid:#A18475;--h-near:#6B5347;--h-ground:#5A4238;
          --h-star:#FFFFFF;--h-star-d:none;--h-smoke:#C0B0A2;
          --h-vane:#3C2B24;--h-accent:#EF4444;
          --h-ink:44 34 30;--h-ink-shadow:0 1px 10px rgba(255,255,255,0.45);
          --h-veil:248 247 245;
        }
        .dark{
          --h-sky1:#1A1216;--h-sky2:#331A20;--h-sky3:#6E2E26;--h-sky4:#B5502E;--h-sky5:#D3703A;
          --h-sun1:#E27A3E;--h-sun2:#F29B55;--h-sun3:#FFC98A;
          --h-far:#47201F;--h-mid:#2C1317;--h-near:#150A0D;--h-ground:#100609;
          --h-star:#F6E7D8;--h-star-d:block;--h-smoke:#C9B4A6;
          --h-vane:#120709;--h-accent:#EF4444;
          --h-ink:255 255 255;--h-ink-shadow:0 2px 16px rgba(0,0,0,0.3);
          --h-veil:15 13 12;
        }
        .hacker{
          --h-sky1:#030A08;--h-sky2:#06130E;--h-sky3:#0A241A;--h-sky4:#0E3A28;--h-sky5:#11543A;
          --h-sun1:#0FA870;--h-sun2:#2FE49A;--h-sun3:#C9FFE9;
          --h-far:#0B231A;--h-mid:#071810;--h-near:#040C09;--h-ground:#030906;
          --h-star:#8FFFD2;--h-star-d:block;--h-smoke:#2E6B52;
          --h-vane:#010503;--h-accent:#00FF9F;
          --h-ink:224 255 240;--h-ink-shadow:0 2px 16px rgba(0,0,0,0.5);
          --h-veil:15 13 12;
        }
        .hf{fill:var(--h-far)}.hm{fill:var(--h-mid)}.hn{fill:var(--h-near)}.hg{fill:var(--h-ground)}
        .hsu1{fill:var(--h-sun1)}.hsu2{fill:var(--h-sun2)}.hsu3{fill:var(--h-sun3)}
        .hsm{fill:var(--h-smoke)}.hv{fill:var(--h-vane)}.hvs{stroke:var(--h-vane)}.hns{stroke:var(--h-near)}
        .hacc{fill:var(--h-accent)}
        .hero-scan{display:none}
        html.hacker .hero-scan{display:block}
        .hero-star{fill:var(--h-star);display:var(--h-star-d,block);animation:hero-twinkle 4.5s ease-in-out infinite}
        .hero-vane{animation:hero-sway 9s ease-in-out infinite;transform-box:view-box;transform-origin:460.5px 374px}
        .hero-smoke{animation:hero-rise 8s linear infinite}
        @keyframes hero-twinkle{0%,100%{opacity:.25}50%{opacity:.9}}
        @keyframes hero-sway{0%,100%{transform:rotate(-5deg)}50%{transform:rotate(4deg)}}
        @keyframes hero-rise{0%{transform:translate(0,0);opacity:0}12%{opacity:.4}100%{transform:translate(10px,-60px);opacity:0}}
        @media (prefers-reduced-motion:reduce){.hero-star,.hero-vane,.hero-smoke{animation:none}}
      `}</style>
      <defs>
        <linearGradient id="hero-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--h-sky1)' }} />
          <stop offset="0.42" style={{ stopColor: 'var(--h-sky2)' }} />
          <stop offset="0.66" style={{ stopColor: 'var(--h-sky3)' }} />
          <stop offset="0.86" style={{ stopColor: 'var(--h-sky4)' }} />
          <stop offset="1" style={{ stopColor: 'var(--h-sky5)' }} />
        </linearGradient>
        <pattern id="hero-scan" width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="1.5" fill="#00FF9F" />
        </pattern>
      </defs>

      {/* Sky */}
      <rect x="0" y="0" width="1440" height="520" fill="url(#hero-sky)" />

      {/* Stars (night themes only — --h-star-d) */}
      <g>
        <circle className="hero-star" cx="120" cy="60" r="1.8" />
        <circle className="hero-star" cx="240" cy="110" r="1.4" style={{ animationDelay: '-1.2s' }} />
        <circle className="hero-star" cx="360" cy="50" r="2" style={{ animationDelay: '-2.6s' }} />
        <circle className="hero-star" cx="470" cy="130" r="1.4" style={{ animationDelay: '-0.6s' }} />
        <circle className="hero-star" cx="580" cy="70" r="1.7" style={{ animationDelay: '-3.4s' }} />
        <circle className="hero-star" cx="700" cy="40" r="1.9" style={{ animationDelay: '-1.8s' }} />
        <circle className="hero-star" cx="820" cy="100" r="1.4" style={{ animationDelay: '-4s' }} />
        <circle className="hero-star" cx="940" cy="60" r="1.8" style={{ animationDelay: '-2.2s' }} />
        <circle className="hero-star" cx="1060" cy="45" r="1.5" style={{ animationDelay: '-0.9s' }} />
        <circle className="hero-star" cx="1170" cy="90" r="1.4" style={{ animationDelay: '-3s' }} />
        <circle className="hero-star" cx="1290" cy="55" r="1.9" style={{ animationDelay: '-1.5s' }} />
        <circle className="hero-star" cx="1380" cy="120" r="1.4" style={{ animationDelay: '-2.8s' }} />
        <circle className="hero-star" cx="200" cy="170" r="1.2" style={{ animationDelay: '-3.8s' }} />
        <circle className="hero-star" cx="660" cy="160" r="1.2" style={{ animationDelay: '-0.3s' }} />
        <circle className="hero-star" cx="1100" cy="150" r="1.2" style={{ animationDelay: '-2s' }} />
      </g>

      {/* Sun (morning sun / setting sun / green moon by theme) */}
      <circle className="hsu1" cx="920" cy="340" r="70" opacity="0.45" />
      <circle className="hsu2" cx="920" cy="340" r="42" opacity="0.8" />
      <circle className="hsu3" cx="920" cy="340" r="22" />

      {/* Far layer — castle district + hills */}
      <g className="hf">
        <rect x="0" y="344" width="60" height="46" />
        <rect x="40" y="330" width="340" height="60" />
        <rect x="70" y="302" width="26" height="28" />
        <polygon points="64,302 83,283 102,302" />
        <polygon points="200,330 214,240 228,330" />
        <polygon points="252,330 266,236 280,330" />
        <rect x="228" y="272" width="24" height="58" />
        <polygon points="224,272 240,248 256,272" />
        <path d="M380,390 Q560,352 760,362 T1120,352 T1440,360 L1440,390 Z" />
      </g>

      {/* Mid layer — rooftop band, St Nicholas dome, Týn church */}
      <g className="hm">
        <path d="M0,460 L0,392 L70,392 L96,368 L122,392 L200,392 L200,380 L268,380 L268,392 L420,392 L420,384 L520,384 L544,366 L568,384 L640,384 L640,392 L780,392 L806,372 L832,392 L900,392 L900,386 L980,386 L980,392 L1240,392 L1240,382 L1330,382 L1330,392 L1440,392 L1440,460 Z" />
        <rect x="560" y="386" width="76" height="74" />
        <path d="M556,388 Q598,330 640,388 Z" />
        <rect x="592" y="318" width="13" height="22" />
        <polygon points="588,318 598.5,300 609,318" />
        <rect x="1040" y="366" width="150" height="94" />
        <rect x="1052" y="308" width="36" height="58" />
        <polygon points="1052,308 1070,206 1088,308" />
        <polygon points="1046,308 1052,280 1058,308" />
        <polygon points="1082,308 1088,280 1094,308" />
        <rect x="1130" y="314" width="36" height="52" />
        <polygon points="1130,314 1148,216 1166,314" />
        <polygon points="1124,314 1130,286 1136,314" />
        <polygon points="1160,314 1166,286 1172,314" />
      </g>

      {/* Near layer — foreground buildings */}
      <g className="hn">
        <polygon points="0,520 0,452 52,408 104,452 104,520" />
        <polygon points="104,520 104,464 176,408 248,464 248,520" />
        <rect x="248" y="448" width="110" height="72" />
        <polygon points="358,520 358,462 425,400 492,462 492,520" />
        <rect x="492" y="456" width="130" height="64" />
        <polygon points="622,520 622,470 700,410 778,470 778,520" />
        <rect x="686" y="446" width="36" height="26" />
        <polygon points="680,446 704,430 728,446" />
        <rect x="778" y="452" width="140" height="68" />
        <polygon points="918,520 918,468 986,414 1054,468 1054,520" />
        <rect x="1054" y="458" width="386" height="62" />
        <rect x="1240" y="422" width="52" height="98" />
        <polygon points="1240,422 1266,394 1292,422" />
        <rect x="210" y="428" width="16" height="44" />
        <rect x="452" y="396" width="17" height="48" />
        <rect x="742" y="424" width="15" height="52" />
      </g>

      {/* Chimney smoke */}
      <g className="hsm">
        <circle className="hero-smoke" cx="218" cy="420" r="5" />
        <circle className="hero-smoke" cx="218" cy="420" r="6.5" style={{ animationDelay: '-2.4s' }} />
        <circle className="hero-smoke" cx="218" cy="420" r="8" style={{ animationDelay: '-4.8s' }} />
      </g>

      {/* Rooster weathervane (brand nod) — pole is static, vane sways */}
      <line className="hvs" x1="460.5" y1="396" x2="460.5" y2="364" strokeWidth="2" />
      <g className="hero-vane">
        <line className="hvs" x1="441" y1="374" x2="480" y2="374" strokeWidth="1.8" />
        <polygon className="hv" points="441,370.5 432,374 441,377.5" />
        <line className="hvs" x1="474" y1="370" x2="480" y2="374" strokeWidth="1.8" />
        <line className="hvs" x1="474" y1="378" x2="480" y2="374" strokeWidth="1.8" />
        <path className="hv" d="M452,364 Q439,346 446,336 Q449,349 456,354 Z" />
        <path className="hv" d="M455,357 Q446,342 454,334 Q455,348 462,352 Z" />
        <ellipse className="hv" cx="465" cy="356" rx="11" ry="9.5" />
        <path className="hv" d="M470,349 Q473,342 478,339 L481,346 Q477,352 472,353 Z" />
        <circle className="hv" cx="478.5" cy="341" r="5" />
        <path className="hacc" d="M475.5,336 Q476,331.5 479,333 Q479.5,330 482,332 Q483.5,330 484,333.5 L482.5,337 Z" />
        <polygon className="hacc" points="483,340 489,341.5 483,343.5" opacity="0.9" />
        <circle className="hacc" cx="482.5" cy="346" r="2" opacity="0.85" />
      </g>

      {/* Antenna */}
      <line className="hns" x1="890" y1="452" x2="890" y2="414" strokeWidth="2" />
      <line className="hns" x1="882" y1="422" x2="898" y2="422" strokeWidth="1.6" />
      <line className="hns" x1="884" y1="430" x2="896" y2="430" strokeWidth="1.6" />
      <circle className="hn" cx="890" cy="412" r="2" />

      {/* Ground strip */}
      <rect className="hg" x="0" y="512" width="1440" height="8" />

      {/* CRT scanlines — hacker theme only */}
      <rect className="hero-scan" x="0" y="0" width="1440" height="520" fill="url(#hero-scan)" opacity="0.05" />
    </svg>
  )
}
